import { FC } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import { Stack, Box, Typography, Alert } from '@mui/material';
import { IconAlertTriangle } from '@tabler/icons-react';
import { ExtendedDialog } from 'ui-component/extended/ExtendedDialog';
import {
  MoveWarningData,
  MoveWarningType,
} from 'views/warehouse/MoveWarningContext/types';
import { ReasonTitles } from 'views/warehouse/MoveWarningContext/constants';
import { UnassignedBinContent } from 'views/warehouse/MoveWarningContext/components/WarningContent/UnassignedBinContent';
import { AssignmentMismatchContent } from 'views/warehouse/MoveWarningContext/components/WarningContent/AssignmentMismatchContent';
import { CustomerMismatchContent } from 'views/warehouse/MoveWarningContext/components/WarningContent/CustomerMismatchContent';
import { DefaultWarningContent } from 'views/warehouse/MoveWarningContext/components/WarningContent/DefaultWarningContent';
import { OrgDisplay } from 'views/warehouse/OrgAndTeamContext';
import { getOrgId } from 'views/warehouse/MoveWarningContext/utils';

type MoveWarningDialogProps = {
  open: boolean;
  warningData: MoveWarningData | null;
  onDismiss: () => void;
};

export const MoveWarningDialog: FC<MoveWarningDialogProps> = ({
  open,
  warningData,
  onDismiss,
}) => {
  const theme = useTheme();

  const getDialogColors = () => {
    if (!warningData)
      return {
        border: theme.palette.error.main,
        background: alpha(theme.palette.error.main, 0.08),
      };

    switch (warningData.reason) {
      case MoveWarningType.UNASSIGNED_BIN:
        return {
          border: theme.palette.warning.main,
          background: alpha(theme.palette.warning.main, 0.08),
        };
      default:
        return {
          border: theme.palette.error.main,
          background: alpha(theme.palette.error.main, 0.08),
        };
    }
  };

  const getTitleColors = () => {
    if (!warningData)
      return {
        background: alpha(theme.palette.error.main, 0.08),
        text: theme.palette.error.main,
      };

    switch (warningData.reason) {
      case MoveWarningType.UNASSIGNED_BIN:
        return {
          background: alpha(theme.palette.warning.main, 0.08),
          text: theme.palette.warning.main,
        };
      default:
        return {
          background: alpha(theme.palette.error.main, 0.08),
          text: theme.palette.error.main,
        };
    }
  };

  const getAlertProps = () => {
    if (!warningData)
      return {
        severity: 'error' as const,
        icon: <IconAlertTriangle color={theme.palette.error.main} />,
      };

    switch (warningData.reason) {
      case MoveWarningType.UNASSIGNED_BIN:
        return {
          severity: 'warning' as const,
          icon: <IconAlertTriangle color={theme.palette.warning.main} />,
        };
      default:
        return {
          severity: 'error' as const,
          icon: <IconAlertTriangle color={theme.palette.error.main} />,
        };
    }
  };

  const getWarningMessage = () => {
    if (!warningData) return '';

    switch (warningData.reason) {
      case MoveWarningType.CUSTOMER_MISMATCH:
        return (
          <>
            This stock lot belongs to{' '}
            <OrgDisplay orgId={getOrgId(warningData.stockLot.org) || ''} />, but{' '}
            {`you're`}
            trying to move it to a location that belongs to{' '}
            <OrgDisplay orgId={getOrgId(warningData.destination.org) || ''} />.
          </>
        );
      case MoveWarningType.ASSIGNMENT_MISMATCH:
        return `This stock lot has assignment mismatches with the destination location.`;
      case MoveWarningType.UNASSIGNED_BIN:
        return `This bin needs to be assigned a purpose to match the stock lot's handling request.`;
      default:
        return 'An error occurred while validating the move.';
    }
  };

  const getWarningContent = () => {
    if (!warningData) return null;

    switch (warningData.reason) {
      case MoveWarningType.UNASSIGNED_BIN:
        return (
          <UnassignedBinContent
            warningData={warningData}
            onDismiss={onDismiss}
          />
        );
      case MoveWarningType.ASSIGNMENT_MISMATCH:
        return <AssignmentMismatchContent warningData={warningData} />;
      case MoveWarningType.CUSTOMER_MISMATCH:
        return <CustomerMismatchContent warningData={warningData} />;
      default:
        return <DefaultWarningContent />;
    }
  };

  const colors = getDialogColors();
  const titleColors = getTitleColors();
  const alertProps = getAlertProps();

  return (
    <ExtendedDialog
      open={open}
      onCloseDialog={() => {}}
      title={
        warningData ? (
          <Box sx={{ bgcolor: titleColors.background, p: 3, width: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <IconAlertTriangle color={titleColors.text} />
              <Typography variant="h3" color={titleColors.text}>
                {ReasonTitles[warningData.reason]}
              </Typography>
            </Stack>
          </Box>
        ) : null
      }
      hideCloseButton
      noActions
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialogContent-root': {
          padding: 0,
        },
        '& .MuiDialog-paper': {
          padding: 0,
          border: '1px solid',
          borderColor: colors.border,
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          p: 3,
          bgcolor: colors.background,
          height: '100%',
        }}
      >
        <Alert
          severity={alertProps.severity}
          variant="outlined"
          icon={alertProps.icon}
          sx={{
            borderWidth: '2px',
            '& .MuiAlert-message': {
              fontSize: '1.1rem',
              fontWeight: 500,
            },
          }}
        >
          <Typography
            variant="body1"
            color={theme.palette.text.primary}
            sx={{ fontSize: '1.1rem' }}
          >
            {getWarningMessage()}
          </Typography>
        </Alert>

        {getWarningContent()}
      </Stack>
    </ExtendedDialog>
  );
};
